import { toastNotifications } from '@uag/react-core';
import { type Dispatch, type SetStateAction, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/shallow';

import { type ArticleGroupPlacement, SolvePanelPlacementResponseErrorCode } from 'api/models';
import { useSolvePanelPlacement } from 'api/queries/forming/forming';
import { buildSolvePanelPlacementRequest } from 'app/modules/wallforming/components/setting-container/panel-placement/utils/buildSolvePanelPlacementRequest';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { EMPTY_GUID } from 'shared/constants';

interface AutomatedPanelPlacementProps {
    setLoadingState: Dispatch<SetStateAction<boolean>>;
}

export const useGetAutomatedPanelPlacement = ({ setLoadingState }: AutomatedPanelPlacementProps) => {
    const { t } = useTranslation();
    const solvePanelPlacementMutation = useSolvePanelPlacement();
    const isPendingRef = useRef<boolean>(false);

    const { selectedPoiIndex, setSolverDebugInfo } = usePlacementStore(
        useShallow((state) => {
            const selectedPoiIndex = state.poiSelection.current.index;
            return {
                selectedPoiIndex,
                setSolverDebugInfo: state.setSolverDebugInfo
            };
        })
    );

    const [searchParams] = useSearchParams();
    const sessionguid = useMemo(() => searchParams.get('formingsession') ?? EMPTY_GUID, [searchParams]);

    return useCallback(async () => {
        if (selectedPoiIndex === undefined || isPendingRef.current) {
            return;
        }

        isPendingRef.current = true;
        setLoadingState(true);

        try {
            const state = usePlacementStore.getState();
            const payload = buildSolvePanelPlacementRequest({
                state,
                sessionId: sessionguid,
                systemId: import.meta.env.VITE_DEFAULT_SYSTEM_ID,
                poiIndex: selectedPoiIndex
            });

            const response = await solvePanelPlacementMutation.mutateAsync({
                data: payload
            });

            const normalizedResponse =
                typeof response === 'object' && response !== null && 'data' in response
                    ? (response as { data: unknown }).data
                    : response;

            const solveResult = normalizedResponse as {
                accepted?: boolean;
                errorCode?: string;
                message?: string;
                placements?: ArticleGroupPlacement[];
            };

            const accepted = solveResult.accepted === true;
            const errorCode =
                (solveResult.errorCode as SolvePanelPlacementResponseErrorCode | undefined) ??
                SolvePanelPlacementResponseErrorCode.None;
            const message = solveResult.message ?? '';

            setSolverDebugInfo({
                accepted,
                errorCode,
                message
            });

            if (!accepted) {
                toastNotifications.error({
                    title: t('error'),
                    message: `${errorCode}: ${message}`
                });
                return;
            } else {
                toastNotifications.success({ title: t('success'), message: t('successPanelPlacement') });
            }
        } catch {
            toastNotifications.error({ title: t('error'), message: t('errorAutoPanelPlacement') });
        } finally {
            isPendingRef.current = false;
            setLoadingState(false);
        }
    }, [selectedPoiIndex, sessionguid, setLoadingState, setSolverDebugInfo, solvePanelPlacementMutation, t]);
};
